import React from 'react';
import { useTranslation } from 'react-i18next';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import menu from '@/assets/icons/menu.svg';
import menuWhite from '@/assets/icons/side-menu-white.svg';
import styles from './ui.module.css';

interface CustomMenuProps {
  /**
   * `danger` marks the destructive item - a cancellation - in the warning colour. `disabled` keeps
   * an action in view that can't be taken right now, with `hint` saying why, instead of hiding it.
   */
  items: { icon?: string; text: string; onClick: () => void; danger?: boolean; disabled?: boolean; hint?: string }[];
  variant?: 'white' | 'black';
  fillCell?: boolean;
}

export default function CustomMenu({ items, variant = 'black', fillCell }: CustomMenuProps) {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (e: React.MouseEvent<HTMLLIElement>) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  return (
    <>
      <button 
        onClick={handleClick}
        className={fillCell ? "flex items-center justify-center -mx-4 w-[calc(100%+2rem)]" : ""}
        style={fillCell ? { height: 'var(--data-table-row-height, 72px)' } : undefined}
      >
        <img src={variant === 'white' ? menuWhite : menu} />
      </button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: i18n.language === 'en' ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: i18n.language === 'en' ? 'right' : 'left',
        }}
        className={styles.menu}
        slotProps={{
          list: { className: styles.menu },
          paper: { className: styles['menu-paper'] },
        }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.text}
            disabled={item.disabled}
            className={`${styles['menu-item']} ${item.danger ? styles['menu-item-danger'] : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              item.onClick();
              handleClose(e);
            }}
          >
            {item.icon && <img src={item.icon} alt={item.text} />}
            {item.hint ? (
              <span className={styles['menu-item-text']}>
                {item.text}
                <span className={styles['menu-item-hint']}>{item.hint}</span>
              </span>
            ) : (
              item.text
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
